import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft,
  Star,
  Eye,
  Target,
  ArrowUp,
  MessageCircle,
  Network,
  Sliders,
  Zap,
  Settings,
  Layers,
  Users,
  FileText,
  Grid,
  Compass,
  Shapes,
  BarChart3,
  Sparkles,
  Palette,
  BookOpen,
  Smartphone,
  Accessibility,
  Type,
  MessageSquare,
} from 'lucide-react';

export const ComprehensiveVisualIdentitySystem: React.FC = () => {
  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <Link to="/elevation/lab" className="inline-flex items-center gap-2 text-consulting-slate-blue-600 hover:text-consulting-slate-blue-700 mb-4">
          <ArrowLeft className="h-4 w-4" />
          Back to Lab
        </Link>
        <div className="flex items-center gap-3 mb-4">
          <div className="px-4 py-2 font-bold text-xl bg-consulting-slate-blue-100 text-consulting-slate-blue-800">
            Lab
          </div>
          <Star className="h-10 w-10 text-consulting-slate-blue-600" />
          <div>
            <h1 className="text-3xl font-bold text-charcoal-900">Comprehensive Visual Identity System</h1>
            <p className="text-charcoal-600">Complete brand system with rules, rationale, and examples</p>
          </div>
        </div>
        <p className="text-lg text-charcoal-700">
          A complete brand system that rivals the world's best agencies. Every component has rules, rationale, and real-world examples.
        </p>
      </div>

      {/* Core System Components */}
      <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-charcoal-200 p-8">
        <h2 className="text-2xl font-bold text-charcoal-900 mb-6 text-center">Core System Components</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          <a href="/elevation/best-practices-design-principles" className="group">
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-[var(--mustard)] p-6 text-center hover:shadow-lg transition-shadow">
              <Star className="h-12 w-12 text-[var(--mustard)] mx-auto mb-4" />
              <h3 className="text-xl font-bold text-charcoal-900 mb-2">Best Practices & Principles</h3>
              <p className="text-charcoal-600 text-sm">Golden ratio, Peak Priorities differentiators, and core design principles</p>
            </div>
          </a>
          <a href="/elevation/visual-inspiration-direction" className="group">
            <div className="bg-gradient-to-br from-consulting-slate-blue-50 to-sage-50 border-2 border-consulting-slate-blue-200 p-6 text-center hover:shadow-lg transition-shadow">
              <Eye className="h-12 w-12 text-consulting-slate-blue-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-charcoal-900 mb-2">Visual Inspiration & Direction</h3>
              <p className="text-charcoal-600 text-sm">Inspiration models, mood boards, and visual direction for Peak Priorities</p>
            </div>
          </a>
          <a href="/elevation/visual-grammar-system" className="group">
            <div className="bg-gradient-to-br from-sage-50 to-amber-50 border-2 border-sage-200 p-6 text-center hover:shadow-lg transition-shadow">
              <Target className="h-12 w-12 text-sage-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-charcoal-900 mb-2">Visual Grammar System</h3>
              <p className="text-charcoal-600 text-sm">Systematic approach to visual communication with primitives, syntax, and rules</p>
            </div>
          </a>
          <a href="/elevation/ladder-of-inference-demo" className="group">
            <div className="bg-gradient-to-br from-amber-50 to-consulting-slate-blue-50 border-2 border-[var(--mustard)] p-6 text-center hover:shadow-lg transition-shadow">
              <ArrowUp className="h-12 w-12 text-[var(--mustard)] mx-auto mb-4" />
              <h3 className="text-xl font-bold text-charcoal-900 mb-2">Ladder of Inference Demo</h3>
              <p className="text-charcoal-600 text-sm">Format-adaptive component demonstrating visual grammar across multiple contexts</p>
            </div>
          </a>
          <a href="/elevation/visual-verbal-integration" className="group">
            <div className="bg-gradient-to-br from-sage-50 to-amber-50 border-2 border-sage-200 p-6 text-center hover:shadow-lg transition-shadow">
              <MessageCircle className="h-12 w-12 text-sage-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-charcoal-900 mb-2">Visual-Verbal Integration</h3>
              <p className="text-charcoal-600 text-sm">Purpose-driven content combining visual grammar with verbal communication for different contexts</p>
            </div>
          </a>
          <a href="/m3-ontology" className="group">
            <div className="bg-gradient-to-br from-consulting-slate-blue-50 to-sage-50 border-2 border-consulting-slate-blue-200 p-6 text-center hover:shadow-lg transition-shadow">
              <Network className="h-12 w-12 text-consulting-slate-blue-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-charcoal-900 mb-2">M3 Ontology</h3>
              <p className="text-charcoal-600 text-sm">What exists: categories, structures, relationships, and systems within the domain</p>
            </div>
          </a>
          <a href="/m3-modality" className="group">
            <div className="bg-gradient-to-br from-amber-50 to-consulting-slate-blue-50 border-2 border-[var(--mustard)] p-6 text-center hover:shadow-lg transition-shadow">
              <Sliders className="h-12 w-12 text-[var(--mustard)] mx-auto mb-4" />
              <h3 className="text-xl font-bold text-charcoal-900 mb-2">M3 Modality</h3>
              <p className="text-charcoal-600 text-sm">How properties vary: attributes, states, contexts, constraints, and identity persistence</p>
            </div>
          </a>
          <a href="/m3-dynamics" className="group">
            <div className="bg-gradient-to-br from-sage-50 to-amber-50 border-2 border-sage-200 p-6 text-center hover:shadow-lg transition-shadow">
              <Zap className="h-12 w-12 text-sage-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-charcoal-900 mb-2">M3 Dynamics</h3>
              <p className="text-charcoal-600 text-sm">How things change: rules, processes, causal mechanisms, and uncertainty factors</p>
            </div>
          </a>
          <a href="/m3-evaluation" className="group">
            <div className="bg-gradient-to-br from-consulting-slate-blue-50 to-sage-50 border-2 border-consulting-slate-blue-200 p-6 text-center hover:shadow-lg transition-shadow">
              <Target className="h-12 w-12 text-consulting-slate-blue-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-charcoal-900 mb-2">M3 Evaluation</h3>
              <p className="text-charcoal-600 text-sm">Why it matters: purpose, value criteria, measurement approaches, and adaptation mechanisms</p>
            </div>
          </a>
          <a href="/m3-mapping-interface" className="group">
            <div className="bg-gradient-to-br from-amber-50 to-consulting-slate-blue-50 border-2 border-[var(--mustard)] p-6 text-center hover:shadow-lg transition-shadow">
              <Settings className="h-12 w-12 text-[var(--mustard)] mx-auto mb-4" />
              <h3 className="text-xl font-bold text-charcoal-900 mb-2">M3 Mapping Interface</h3>
              <p className="text-charcoal-600 text-sm">Select and assign M3 metadata to generate visual signature elements across artifact types</p>
            </div>
          </a>
          <a href="/elevation/ladder-of-inference-m2-model" className="group">
            <div className="bg-gradient-to-br from-sage-50 to-amber-50 border-2 border-sage-200 p-6 text-center hover:shadow-lg transition-shadow">
              <Layers className="h-12 w-12 text-sage-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-charcoal-900 mb-2">Ladder of Inference M2 Model</h3>
              <p className="text-charcoal-600 text-sm">Concrete M2 model with M3 metadata applied, demonstrating visual signature elements across formats</p>
            </div>
          </a>
          <a href="/elevation/johari-window-m2-model" className="group">
            <div className="bg-gradient-to-br from-consulting-slate-blue-50 to-sage-50 border-2 border-consulting-slate-blue-200 p-6 text-center hover:shadow-lg transition-shadow">
              <Users className="h-12 w-12 text-consulting-slate-blue-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-charcoal-900 mb-2">Johari Window M2 Model</h3>
              <p className="text-charcoal-600 text-sm">Self-awareness framework with M3 metadata, showing how visual signatures encode meaning</p>
            </div>
          </a>
          <a href="/elevation/logical-levels-m2-model" className="group">
            <div className="bg-gradient-to-br from-amber-50 to-consulting-slate-blue-50 border-2 border-[var(--mustard)] p-6 text-center hover:shadow-lg transition-shadow">
              <Layers className="h-12 w-12 text-[var(--mustard)] mx-auto mb-4" />
              <h3 className="text-xl font-bold text-charcoal-900 mb-2">Logical Levels M2 Model</h3>
              <p className="text-charcoal-600 text-sm">Change framework with M3 metadata, demonstrating hierarchical visual patterns</p>
            </div>
          </a>
          <a href="/tool-ecosystem" className="group">
            <div className="bg-gradient-to-br from-sage-50 to-amber-50 border-2 border-sage-200 p-6 text-center hover:shadow-lg transition-shadow">
              <Network className="h-12 w-12 text-sage-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-charcoal-900 mb-2">Tool Ecosystem</h3>
              <p className="text-charcoal-600 text-sm">Explore how coaching tools connect through shared visual grammar and semantic meaning</p>
            </div>
          </a>
          <a href="/elevation/artifact-gallery" className="group">
            <div className="bg-gradient-to-br from-consulting-slate-blue-50 to-sage-50 border-2 border-consulting-slate-blue-200 p-6 text-center hover:shadow-lg transition-shadow">
              <FileText className="h-12 w-12 text-consulting-slate-blue-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-charcoal-900 mb-2">Artifact Gallery</h3>
              <p className="text-charcoal-600 text-sm">M1 representations - actual field cards, slides, and PDFs with M3 metadata and visual signatures</p>
            </div>
          </a>
          <a href="/elevation/visual-design-grammar" className="group">
            <div className="bg-gradient-to-br from-amber-50 to-consulting-slate-blue-50 border-2 border-[var(--mustard)] p-6 text-center hover:shadow-lg transition-shadow">
              <Layers className="h-12 w-12 text-[var(--mustard)] mx-auto mb-4" />
              <h3 className="text-xl font-bold text-charcoal-900 mb-2">Visual Design Grammar</h3>
              <p className="text-charcoal-600 text-sm">Systematic approach to visual communication encoding meaning through colors, shapes, lines, fills, patterns, and connections</p>
            </div>
          </a>
        </div>
      </div>

      {/* Visual Elements */}
      <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-charcoal-200 p-8">
        <h2 className="text-2xl font-bold text-charcoal-900 mb-6 text-center">Visual Elements</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          <a href="/elevation/logo-system" className="group">
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-[var(--mustard)] p-6 text-center hover:shadow-lg transition-shadow">
              <Compass className="h-12 w-12 text-[var(--mustard)] mx-auto mb-4" />
              <h3 className="text-xl font-bold text-charcoal-900 mb-2">Logo System</h3>
              <p className="text-charcoal-600 text-sm">Primary, secondary, icon, horizontal, vertical, favicon, lockups, and placement rules</p>
            </div>
          </a>
          <a href="/elevation/layout-grid-system" className="group">
            <div className="bg-gradient-to-br from-consulting-slate-blue-50 to-sage-50 border-2 border-consulting-slate-blue-200 p-6 text-center hover:shadow-lg transition-shadow">
              <Grid className="h-12 w-12 text-consulting-slate-blue-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-charcoal-900 mb-2">Layout & Grid</h3>
              <p className="text-charcoal-600 text-sm">Spacing, alignment, margins, gutters, and white space rules for all applications</p>
            </div>
          </a>
          <a href="/elevation/motion-animation-principles" className="group">
            <div className="bg-gradient-to-br from-amber-50 to-consulting-slate-blue-50 border-2 border-[var(--mustard)] p-6 text-center hover:shadow-lg transition-shadow">
              <Zap className="h-12 w-12 text-[var(--mustard)] mx-auto mb-4" />
              <h3 className="text-xl font-bold text-charcoal-900 mb-2">Motion & Animation</h3>
              <p className="text-charcoal-600 text-sm">Element transitions, entrance/exit patterns, and brand-appropriate animation guidelines</p>
            </div>
          </a>
          <a href="/elevation/tone-voice-system" className="group">
            <div className="bg-gradient-to-br from-sage-50 to-amber-50 border-2 border-sage-200 p-6 text-center hover:shadow-lg transition-shadow">
              <MessageSquare className="h-12 w-12 text-sage-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-charcoal-900 mb-2">Tone & Voice</h3>
              <p className="text-charcoal-600 text-sm">Communication guidelines for consistent, effective messaging across all touchpoints</p>
            </div>
          </a>
          <a href="/elevation/patterns-background-elements" className="group">
            <div className="bg-gradient-to-br from-consulting-slate-blue-50 to-sage-50 border-2 border-consulting-slate-blue-200 p-6 text-center hover:shadow-lg transition-shadow">
              <Shapes className="h-12 w-12 text-consulting-slate-blue-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-charcoal-900 mb-2">Patterns & Backgrounds</h3>
              <p className="text-charcoal-600 text-sm">Geometric patterns, overlays, textures, and background elements for slides and web</p>
            </div>
          </a>
          <a href="/elevation/infographic-data-viz-standards" className="group">
            <div className="bg-gradient-to-br from-amber-50 to-consulting-slate-blue-50 border-2 border-[var(--mustard)] p-6 text-center hover:shadow-lg transition-shadow">
              <BarChart3 className="h-12 w-12 text-[var(--mustard)] mx-auto mb-4" />
              <h3 className="text-xl font-bold text-charcoal-900 mb-2">Data Visualization</h3>
              <p className="text-charcoal-600 text-sm">Charts, graphs, bar shapes, label treatments, color coding, and annotation styles</p>
            </div>
          </a>
          <a href="/elevation/icon-library" className="group">
            <div className="bg-gradient-to-br from-sage-50 to-amber-50 border-2 border-sage-200 p-6 text-center hover:shadow-lg transition-shadow">
              <Sparkles className="h-12 w-12 text-sage-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-charcoal-900 mb-2">Icon Library</h3>
              <p className="text-charcoal-600 text-sm">Comprehensive icon system with semantic meaning and usage guidelines</p>
            </div>
          </a>
          <a href="/elevation/brand-elements" className="group">
            <div className="bg-gradient-to-br from-consulting-slate-blue-50 to-sage-50 border-2 border-consulting-slate-blue-200 p-6 text-center hover:shadow-lg transition-shadow">
              <Palette className="h-12 w-12 text-consulting-slate-blue-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-charcoal-900 mb-2">Brand Elements</h3>
              <p className="text-charcoal-600 text-sm">Logos, patterns, and visual identity elements for consistent branding</p>
            </div>
          </a>
        </div>
      </div>

      {/* Application & Guidelines */}
      <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-charcoal-200 p-8">
        <h2 className="text-2xl font-bold text-charcoal-900 mb-6 text-center">Application & Guidelines</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          <a href="/elevation/application-templates" className="group">
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-[var(--mustard)] p-6 text-center hover:shadow-lg transition-shadow">
              <FileText className="h-12 w-12 text-[var(--mustard)] mx-auto mb-4" />
              <h3 className="text-xl font-bold text-charcoal-900 mb-2">Application Templates</h3>
              <p className="text-charcoal-600 text-sm">Pre-built templates for proposals, reports, presentations, social media, and worksheets</p>
            </div>
          </a>
          <a href="/elevation/lab/brand-guidelines-document" className="group">
            <div className="bg-gradient-to-br from-consulting-slate-blue-50 to-sage-50 border-2 border-consulting-slate-blue-200 p-6 text-center hover:shadow-lg transition-shadow">
              <BookOpen className="h-12 w-12 text-consulting-slate-blue-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-charcoal-900 mb-2">Brand Guidelines</h3>
              <p className="text-charcoal-600 text-sm">Comprehensive document with rules, examples, dos/don'ts, assets, and usage scenarios</p>
            </div>
          </a>
          <a href="/elevation/favicon-app-icons" className="group">
            <div className="bg-gradient-to-br from-sage-50 to-amber-50 border-2 border-sage-200 p-6 text-center hover:shadow-lg transition-shadow">
              <Smartphone className="h-12 w-12 text-sage-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-charcoal-900 mb-2">Favicon & App Icons</h3>
              <p className="text-charcoal-600 text-sm">Special rules for tiny digital applications (browser tabs, mobile apps, social avatars)</p>
            </div>
          </a>
          <a href="/elevation/accessibility-rules" className="group">
            <div className="bg-gradient-to-br from-amber-50 to-consulting-slate-blue-50 border-2 border-[var(--mustard)] p-6 text-center hover:shadow-lg transition-shadow">
              <Accessibility className="h-12 w-12 text-[var(--mustard)] mx-auto mb-4" />
              <h3 className="text-xl font-bold text-charcoal-900 mb-2">Accessibility Rules</h3>
              <p className="text-charcoal-600 text-sm">Color contrast, font sizes, alt text, and disability compliance guidelines</p>
            </div>
          </a>
        </div>
      </div>

      {/* Legacy Components */}
      <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-charcoal-200 p-8">
        <h2 className="text-2xl font-bold text-charcoal-900 mb-6 text-center">Legacy Components</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <a href="/elevation/component-library" className="group">
            <div className="bg-gradient-to-br from-sage-50 to-amber-50 border-2 border-sage-200 p-6 text-center hover:shadow-lg transition-shadow">
              <Layers className="h-12 w-12 text-sage-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-charcoal-900 mb-2">Component Library</h3>
              <p className="text-charcoal-600 text-sm">Reusable UI components with code examples and implementation guides</p>
            </div>
          </a>
          <a href="/component-library-v2" className="group">
            <div className="bg-gradient-to-br from-consulting-slate-blue-50 to-sage-50 border-2 border-consulting-slate-blue-200 p-6 text-center hover:shadow-lg transition-shadow">
              <Layers className="h-12 w-12 text-consulting-slate-blue-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-charcoal-900 mb-2">Component Library V2</h3>
              <p className="text-charcoal-600 text-sm">Enhanced component library with advanced features and patterns</p>
            </div>
          </a>
          <a href="/elevation/visual-design-grammar" className="group">
            <div className="bg-gradient-to-br from-amber-50 to-consulting-slate-blue-50 border-2 border-[var(--mustard)] p-6 text-center hover:shadow-lg transition-shadow">
              <Type className="h-12 w-12 text-[var(--mustard)] mx-auto mb-4" />
              <h3 className="text-xl font-bold text-charcoal-900 mb-2">Visual Design Grammar</h3>
              <p className="text-charcoal-600 text-sm">Design grammar and visual language rules for consistent application</p>
            </div>
          </a>
        </div>
      </div>

      {/* Call to Action */}
      <div className="bg-gradient-to-r from-consulting-slate-blue-50 to-sage-50 border-2 border-consulting-slate-blue-200 p-8 text-center">
        <h3 className="text-2xl font-bold text-charcoal-900 mb-3">
          Building a Coherent Knowledge Ecosystem
        </h3>
        <p className="text-charcoal-700 max-w-3xl mx-auto">
          Our design language system ensures that every framework, template, and deliverable reinforces the same mental models. 
          When verbal and visual languages align, clarity emerges—and that's when teams move forward with confidence.
        </p>
      </div>
    </div>
  );
};
